/**
 * sceneLiveVideo — minimal NSPP-native video playback.
 *
 * Just a fullscreen image node whose imageUrl cycles through 201 frames
 * extracted from the selfie video at 12fps. No overlays, no vignette,
 * no REC indicator — the user just wants to see the video cleanly.
 *
 * The imageUrl mutation is the same pattern chase.ts uses for its hero
 * image. NSPP getImageTexture caches the decoded JPG, the compiler
 * sees the change and re-uploads.
 */
import {
  addNode,
  createScene,
  makeImage,
  type Scene,
  type SceneNode,
} from "../sceneGraph";

const FRAME_COUNT = 201;
const FPS = 12;
const FRAME_BASE = "/static/video_frames/frame_";

function pad(n: number): string {
  return String(n).padStart(3, "0");
}

function frameUrl(i: number): string {
  const idx = (i % FRAME_COUNT) + 1;
  return `${FRAME_BASE}${pad(idx)}.jpg`;
}

export function sceneLiveVideo(w: number, h: number): Scene {
  const s = createScene();

  // Fullscreen image — imageUrl cycles through the 201 frames.
  const videoNode = makeImage({
    x: 0, y: 0, w, h,
    imageUrl: frameUrl(0),
    radius: 0,
    zIndex: 0,
  });
  let lastFrameIdx = -1;
  videoNode.update = (n: SceneNode, t: number) => {
    const frameIdx = Math.floor(t * FPS);
    const idx = frameIdx % FRAME_COUNT;
    if (idx !== lastFrameIdx) {
      lastFrameIdx = idx;
      n.imageUrl = frameUrl(frameIdx);
    }
  };
  addNode(s, videoNode);

  return s;
}